// ADR-0053 §3 / V101-4-4: one SSRF guard for every customer-supplied host,
// driven by the connector manifest's `hosts`. Called at create AND on every
// run. Redirects stay `manual` at fetch time (no credential-bearing hop).
// DNS rebinding to a private address is not checkable from a Worker; Workers'
// outbound fetch cannot reach private RFC 1918 ranges, so that residual risk
// is documented (ADR-0053), not coded.
//
// Pure; no runtime imports.
import { hostMatches } from './manifest.js';
export class HostNotAllowedError extends Error {
    reason;
    constructor(reason) {
        super(`Host not allowed: ${reason}`);
        this.reason = reason;
        this.name = 'HostNotAllowedError';
    }
}
/** Suffixes that never name a public host. */
const NON_PUBLIC_SUFFIX = /\.(local|localhost|internal|intranet|lan|home|corp|localdomain|home\.arpa|test|invalid|example)$/;
/** IP literals in any form URL parsing accepts (dotted, decimal, hex, IPv6). */
function isIpLiteral(host) {
    return host.startsWith('[') || /^[0-9.]+$/.test(host) || /^0x[0-9a-f.x]+$/i.test(host) || host.includes(':');
}
/**
 * Validate `raw` as a URL a connector may call. Returns the parsed URL.
 * Throws HostNotAllowedError with a reason otherwise.
 */
export function assertAllowedHost(manifest, raw) {
    // V101-8-2: a kind whose hosts are private never runs in a Worker (the manifest validator
    // enforces runnableIn). Refuse here too so a mis-routed run cannot reach a customer address.
    if (manifest.hosts.customerSupplied?.privateHosts)
        throw new HostNotAllowedError('this connector runs only in the customer-run collector');
    let u;
    try {
        u = new URL(String(raw));
    }
    catch {
        throw new HostNotAllowedError('not a URL');
    }
    const host = u.hostname.toLowerCase();
    if (u.protocol !== 'https:')
        throw new HostNotAllowedError('https only');
    if (u.username || u.password)
        throw new HostNotAllowedError('credentials in the URL');
    if (u.port && u.port !== '443')
        throw new HostNotAllowedError('non-standard port');
    if (isIpLiteral(host))
        throw new HostNotAllowedError('IP addresses are not allowed');
    if (host === 'localhost' || !host.includes('.') || host.endsWith('.') || NON_PUBLIC_SUFFIX.test(host)) {
        throw new HostNotAllowedError('not a public host name');
    }
    if (!/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/.test(host) || host.includes('..'))
        throw new HostNotAllowedError('invalid host name');
    const patterns = [...(manifest.hosts.fixed ?? []), ...(manifest.hosts.customerSupplied?.patterns ?? [])];
    if (patterns.some((p) => hostMatches(host, p)))
        return u;
    if (manifest.hosts.customerSupplied?.anyPublicHost)
        return u;
    throw new HostNotAllowedError(`${host} is not a host this connector calls`);
}
/**
 * V101-8-2 / ADR-0055 §2: the host guard the CUSTOMER-RUN collector applies. Targets
 * live inside the customer's network, so cluster DNS, RFC 1918, loopback and plain
 * http are fine. What is never fine: credentials in the URL, a non-http(s) scheme, and
 * the link-local / cloud-metadata range (a collector must not become a route to an
 * instance-metadata service). Kinds without `privateHosts` keep the public-host guard.
 * Redirects must stay `manual` at fetch time (same rule as the Worker).
 */
export function assertCustomerCollectorHost(manifest, raw) {
    if (!manifest.hosts.customerSupplied?.privateHosts)
        return assertAllowedHost(manifest, raw);
    return assertCollectorUrl(raw);
}
/**
 * The URL checks every customer-collector call shares (private targets, and the in-cluster
 * Kubernetes API server whose address comes from the pod's own environment, not a manifest).
 */
export function assertCollectorUrl(raw) {
    let u;
    try {
        u = new URL(String(raw));
    }
    catch {
        throw new HostNotAllowedError('not a URL');
    }
    if (u.protocol !== 'https:' && u.protocol !== 'http:')
        throw new HostNotAllowedError('http(s) only');
    if (u.username || u.password)
        throw new HostNotAllowedError('credentials in the URL');
    const host = u.hostname.toLowerCase().replace(/^\[|\]$/g, '');
    if (!host)
        throw new HostNotAllowedError('empty host');
    if (host === '0.0.0.0' || host === '::')
        throw new HostNotAllowedError('unspecified address');
    if (/^169\.254\./.test(host) || /^fe80:/i.test(host) || /^::ffff:a9fe:/i.test(host) || host === 'metadata.google.internal' || host === 'metadata' || host.endsWith('.metadata.azure.com') || host === 'instance-data') {
        throw new HostNotAllowedError('link-local / instance-metadata addresses are never called');
    }
    if (!/^[a-z0-9:.-]+$/.test(host) || host.includes('..'))
        throw new HostNotAllowedError('invalid host name');
    return u;
}
/**
 * BL-24 (C-7 L-2): a run-time call outside the hosts the connection declares.
 * Thrown by the posture runner's fetch wrapper before any request is made.
 */
export class UndeclaredHostError extends Error {
    host;
    constructor(host, kind) {
        super(`Nothing was recorded: this ${kind} connection tried to call ${host || 'an invalid URL'}, which is not a host its connector declares.`);
        this.host = host;
        this.name = 'UndeclaredHostError';
    }
}
/**
 * BL-24 (C-7 L-2): check one outbound URL against a connection's declared hosts
 * (`connectorHosts(manifest, config)`: fixed hosts with `{region}` expanded from the
 * connection's config, plus its configured customer host). https only.
 */
export function assertDeclaredHost(declared, kind, raw) {
    let u;
    try {
        u = new URL(String(raw));
    }
    catch {
        throw new UndeclaredHostError('', kind);
    }
    const host = u.hostname.toLowerCase();
    if (u.protocol !== 'https:' || u.username || u.password || (u.port && u.port !== '443'))
        throw new UndeclaredHostError(host, kind);
    if (!declared.some((p) => hostMatches(host, p)))
        throw new UndeclaredHostError(host, kind);
    return u;
}
//# sourceMappingURL=hosts.js.map